import { JetSportBox } from "../../components/BoxView/JetSportBox";
import { SecondaryButton } from "../../components/Buttons/SecondaryButton";
import { PrimaryButton } from "../../components/Buttons/PrimaryButton";
import { BoxHeader2 } from "../../components/BoxView/BoxHeader2";
import { BoxContent } from "../../components/BoxView/BoxContent";
import { BoxFooter } from "../../components/BoxView/BoxFooter";
import { OrderLineList } from "./subcomponents/OrderLineList";
import { PriceTotal } from "./subcomponents/PriceTotal";
import { Order, OrderLine } from "../../../core/models/entities/Order";
import {
  OrderDeliveryForm,
  DeliveryTypeOption,
  PackagingOption,
} from "../order-confirmation/useOrderConfirmationEditViewModel";
import { useProfileContext } from "../../../contexts/ProfileContext";
import { Role } from "../../../core/models/entities/Role";
import { Switch } from "../../components/ui/Switch";
import { useWorkshopMode } from "../../../hooks/useWorkshopMode";
import { useTranslation } from "react-i18next";
import { OrderStatusElement } from "../order-list/components/OrderStatusElement";
import {
  AwaitingPaymentNotice,
  PayNowButton,
} from "../../components/Order/PayNow";

export interface OrderDetailsComponentViewModel {
  orderNo: string;
  orderlines: OrderLine[];
  handleBack: () => void;
  handleConfirm: () => void;
  form: OrderDeliveryForm;
  deliveryTypeGroupList: DeliveryTypeOption[];
  packagingGroupList: PackagingOption[];
  handleOrderFormUpdate: (form: OrderDeliveryForm) => void;
  subTotal?: number;
  vat?: number;
  totalAmount?: number;
  readonly?: boolean;
  order: Order;
  onBack: () => void;
  onReorder: () => void;
  showCustomerPrices: boolean;
  setShowCustomerPrices: (value: boolean) => void;
}

export function OrderDetailsComponent({
  order,
  orderlines,
  subTotal,
  vat,
  totalAmount,
  onBack,
  onReorder,
  readonly,
  showCustomerPrices,
  setShowCustomerPrices,
}: OrderDetailsComponentViewModel) {
  const { t } = useTranslation();
  const { role, organizationId } = useProfileContext();
  // Workshop mode: order stays fully usable as a work instruction, but every
  // price surface (columns, totals, price switches) is removed.
  const workshopMode = useWorkshopMode();
  return (
    <JetSportBox>
      <BoxHeader2
        className={"uppercase text-sm flex justify-between items-center"}
      >
        <p className={"flex items-center gap-3"}>
          <span>
            <span className={"font-bold"}>{t("order")} </span>#
            {order.orderNumber}
          </span>
          {/* Order state was previously invisible on this screen entirely */}
          <OrderStatusElement order={order} />
        </p>
        {role === Role.Vendor &&
          !workshopMode &&
          readonly &&
          order.organization.id !== organizationId && (
            <div className="flex items-center gap-2">
              <label>{t("orderConfirmation.showCustomerPrices")}</label>
              <Switch
                checked={showCustomerPrices}
                onCheckedChange={setShowCustomerPrices}
              />
            </div>
          )}
      </BoxHeader2>
      <BoxContent>
        {/* Stripe prepayment: explain why the order is parked before its lines */}
        <AwaitingPaymentNotice order={order} className={"mt-4"} />
        <div className={"flex-1 flex-col my-10"}>
          <OrderLineList
            hideColumns={
              workshopMode
                ? { unitPrice: true, price: true, subTotal: true }
                : undefined
            }
            orderlines={orderlines}
            currency={order.currency}
            showCustomerPrices={showCustomerPrices}
            readonly={readonly}
          />
          {!workshopMode && (
            <PriceTotal
              subTotal={subTotal || 0}
              vat={vat || 0}
              totalAmount={totalAmount || 0}
              currency={order.currency}
            />
          )}
        </div>
      </BoxContent>
      <BoxFooter className={"justify-between"}>
        <SecondaryButton onClick={onBack}>{t("back")}</SecondaryButton>
        <div className={"flex gap-x-4"}>
          {role !== Role.Admin && (
            <PrimaryButton onClick={onReorder}>
              {t("orderListDetails.reorder")}
            </PrimaryButton>
          )}
          {/* Self-gating: only renders while the order awaits Stripe payment */}
          <PayNowButton order={order} />
        </div>
      </BoxFooter>
    </JetSportBox>
  );
}
